import { ArrowLeft, Loader2 } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import CancelAttemptDialog from '../components/payment-history/CancelAttemptDialog';
import PaymentRequestCard from '../components/payment-history/PaymentRequestCard';
import PortalHeader from '../components/PortalHeader';
import { groupLegacyTransactions } from '../components/payment-history/paymentRequestUtils';
import { paymentAPI, userAPI } from '../services/api';
import { responseData } from '../services/apiResponse';
import { clearAuthenticatedUser } from '../services/authStorage';

export default function PaymentRequestDetail() {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cancelTarget, setCancelTarget] = useState(null);
  const [busyAttemptId, setBusyAttemptId] = useState('');
  const pollFailures = useRef(0);

  const handleLogout = async () => {
    const refreshToken = localStorage.getItem('refresh_token');
    if (refreshToken) {
      try {
        await userAPI.logout(refreshToken);
      } catch {
        // Local sign-out must still complete when the server session is unavailable.
      }
    }
    clearAuthenticatedUser({ includeSession: true });
    navigate('/login', { replace: true });
  };

  const loadRequest = useCallback(async ({ background = false } = {}) => {
    if (!background) setLoading(true);
    setError('');
    try {
      try {
        const response = await paymentAPI.getPaymentRequest(requestId);
        setRequest(responseData(response));
      } catch (requestError) {
        if (requestError.response?.status !== 404 && requestError.response?.status !== 405) throw requestError;
        const response = await paymentAPI.getUserTransactions();
        const legacyRequest = groupLegacyTransactions(responseData(response).transactions || []).find((item) => item.request_id === requestId);
        if (!legacyRequest) throw requestError;
        setRequest(legacyRequest);
      }
      pollFailures.current = 0;
    } catch (requestError) {
      pollFailures.current += 1;
      setError(requestError.response?.data?.detail || requestError.response?.data?.error || 'Payment request could not be loaded.');
    } finally {
      if (!background) setLoading(false);
    }
  }, [requestId]);

  useEffect(() => {
    loadRequest();
  }, [loadRequest]);

  useEffect(() => {
    const shouldPoll = request && (
      request.status === 'pending' || request.status === 'processing' || request.payout_status === 'processing'
    );
    if (!shouldPoll) return undefined;
    let stopped = false;
    let timer;
    const baseDelay = request.status === 'processing' ? 15000 : request.payout_status === 'processing' ? 30000 : 60000;
    const schedule = () => {
      timer = window.setTimeout(async () => {
        if (!stopped && document.visibilityState === 'visible') await loadRequest({ background: true });
        if (!stopped) schedule();
      }, Math.min(120000, baseDelay * (2 ** Math.min(pollFailures.current, 3))));
    };
    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      window.clearTimeout(timer);
      void loadRequest({ background: true }).finally(schedule);
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    schedule();
    return () => {
      stopped = true;
      window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [loadRequest, request]);

  const handleContinue = (item) => {
    navigate(`/review/${encodeURIComponent(item.request_id)}`);
  };

  const confirmCancel = async () => {
    if (!cancelTarget) return;
    setBusyAttemptId(cancelTarget.attempt_id);
    setError('');
    try {
      await paymentAPI.cancelPaymentAttempt(requestId, cancelTarget.attempt_id);
      setCancelTarget(null);
      await loadRequest();
    } catch (requestError) {
      setError(requestError.response?.data?.detail || requestError.response?.data?.error || 'This payment attempt could not be cancelled.');
    } finally {
      setBusyAttemptId('');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <PortalHeader user={{ email: localStorage.getItem('user_email') }} onLogout={handleLogout} />
      <main className="px-4 py-8">
      <div className="mx-auto max-w-7xl">
        <button type="button" onClick={() => navigate('/')} className="inline-flex items-center gap-2 rounded px-2 py-2 text-sm font-medium text-slate-600 hover:bg-white hover:text-slate-900">
          <ArrowLeft className="h-4 w-4" />
          Back to payment requests
        </button>
        <div className="mt-4">
          {loading ? (
            <div className="flex min-h-64 items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-cyan-700" /></div>
          ) : error && !request ? (
            <div className="rounded-md border border-rose-200 bg-rose-50 p-6 text-sm text-rose-700">{error}</div>
          ) : request ? (
            <>
              {error ? <p className="mb-3 rounded border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</p> : null}
              <PaymentRequestCard
                request={request}
                expanded
                busyAttemptId={busyAttemptId}
                onToggle={() => {}}
                onContinue={handleContinue}
                onCancel={setCancelTarget}
              />
            </>
          ) : null}
        </div>
      </div>
      </main>
      <CancelAttemptDialog
        attempt={cancelTarget}
        loading={Boolean(busyAttemptId)}
        error={cancelTarget ? error : ''}
        onCancel={confirmCancel}
        onClose={() => {
          if (!busyAttemptId) {
            setCancelTarget(null);
            setError('');
          }
        }}
      />
    </div>
  );
}
